import { useRef } from 'react';

type ChainedResult<T = unknown, E extends Error = Error> = {
  data: T | null;
  error: E | null;
};

type ChainedCallback<T = unknown> = (
  previousValue: ChainedResult<T>,
) => Promise<T> | T;

function withStandardReturnType<T>(callback: ChainedCallback<T>) {
  return async function callbackWithStandardReturnType({
    data,
    error,
  }: ChainedResult<T>): Promise<ChainedResult<T>> {
    try {
      const result = await Promise.resolve(callback({ data, error }));
      return { data: result, error: null };
    } catch (error) {
      return Promise.resolve({
        data: null,
        error: error as Error,
      });
    }
  };
}

export function useRunWithQueue<T = void>() {
  const queue = useRef<Promise<ChainedResult<T>> | undefined>();

  return function runWithQueue(callback: ChainedCallback<T>) {
    queue.current = !queue.current
      ? withStandardReturnType<T>(callback)({ data: null, error: null })
      : queue.current.then(withStandardReturnType<T>(callback));
  };
}
